import React from 'react';
import { Announcement } from '../Announcement/Announcement';
import ContentMessage from '../../../../_lib/components/ContentMessage/ContentMessage';
import { BlockType } from '../../../../types/api/ElementType';
import { Pagination, PaginationItemWithPageLink, PaginationModifier } from '../../../../_lib/components/Pagination';
import { useAppContext } from '../../../../contexts/AppContext/AppContext';
import bem from '../../../../lib/bemUtils/bem';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { useBlockContext } from '../../../../_lib/BlockFetcher';
import { AnnouncementProps } from '../../Announcement.types';
import { GlobalDocumentIds } from '../../../../types/GlobalDocumentIds';
import { SkipLink } from '../../../../_lib/components/SkipLink';

type AnnouncementList = {
  announcements: AnnouncementProps[];
  pageItems?: PaginationItemWithPageLink[];
};
type ValidBlockTypes =
  | BlockType.currentannouncementsinbox
  | BlockType.currentannouncementsoutbox
  | BlockType.currentmandatoryannouncementsforpage
  | BlockType.allannouncementsoutbox;
export function AnnouncementList({ announcements, pageItems }: AnnouncementList) {
  const { editModeInfo } = useAppContext();
  const { elementType } = useBlockContext<ValidBlockTypes>();
  const { t } = useSonTranslation('Announcement');

  const getVerticalListModifier = () => {
    switch (elementType) {
      case BlockType.currentannouncementsinbox:
      case BlockType.currentannouncementsoutbox:
      case BlockType.currentmandatoryannouncementsforpage:
        return 'announcement-list--vertical';
      default:
        return null;
    }
  };

  const getContainerClassName = () => {
    return bem('announcement-list', getVerticalListModifier());
  };

  const containsPagination = pageItems && pageItems.length > 0;

  if (announcements && announcements.length > 0) {
    return (
      <>
        {/* tabindex needed for vertical lists, announcements can have zero interactive elements */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <div className={getContainerClassName()} tabIndex={getVerticalListModifier() ? 0 : undefined}>
          {containsPagination && <SkipLink id={GlobalDocumentIds.pagination}>{t('skiplink.toPagination')}</SkipLink>}
          <ol className="announcement-list__list">
            {announcements.map((announcement, index) => (
              <li className="announcement-list__list-item" key={announcement.id}>
                <Announcement
                  {...announcement}
                  // editModeInfo only active for announcements in list, not for personal
                  editModeActive={editModeInfo.active && elementType === BlockType.allannouncementsoutbox}
                  elementType={elementType}
                  index={index + 1}
                />
              </li>
            ))}
          </ol>
          {containsPagination && (
            <SkipLink id={announcements[0].id} modifier="bottom">
              {t('skiplink.toFirstAnnouncement')}
            </SkipLink>
          )}
          {containsPagination && <Pagination pageItems={pageItems} modifier={PaginationModifier.BORDER_TOP} />}
        </div>
      </>
    );
  }
  return <ContentMessage text={t('noAnnouncements')} />;
}
